import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import SearchResultItem from "./search/SearchResultItem";

/** Cmd/Ctrl-K global search across every cached content document. */
export default function SearchModal({ open, onClose }) {
  const { lang, isRTL } = useLanguage();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (open) { setQ(""); setResults([]); setCursor(0); }
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setResults([]); return; }
      setLoading(true);
      try {
        const r = await api.get("/search", { params: { q: q.trim(), lang } });
        setResults(r.data.results || []);
        setCursor(0);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, lang, open]);

  const pick = useCallback((r) => {
    onClose();
    navigate(`/chapter/${r.chapter_id}?tab=${r.kind}`);
  }, [onClose, navigate]);

  // Keyboard navigation.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
      if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
      if (e.key === "Enter" && results[cursor]) pick(results[cursor]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, results, cursor, onClose, pick]);

  if (!open) return null;

  const query = q.trim();
  const showEmpty = query.length >= 2 && !loading && results.length === 0;

  return (
    <div
      data-testid="search-modal"
      className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-border overflow-hidden animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <Search className="w-5 h-5 text-sage shrink-0" strokeWidth={1.8} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "ar" ? "ابحث في المقرّر…" : "Search the course…"}
            data-testid="search-input"
            className="flex-1 bg-transparent focus:outline-none text-base placeholder:text-muted-foreground"
          />
          <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
            ESC
          </span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="search-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {query.length < 2 && (
            <div className="p-10 text-center text-sm text-muted-foreground">
              {lang === "ar"
                ? "اكتب كلمتين على الأقل للبحث عبر الدروس والبطاقات والاختبارات."
                : "Type at least 2 characters to search across lessons, flashcards, quizzes, scenarios, and summaries."}
            </div>
          )}
          {query.length >= 2 && loading && (
            <div className="p-10 text-center text-sm text-sage">
              {lang === "ar" ? "جارٍ البحث…" : "Searching…"}
            </div>
          )}
          {showEmpty && (
            <div className="p-10 text-center text-sm text-muted-foreground" data-testid="search-empty">
              {lang === "ar" ? "لا توجد نتائج." : "No results."}
            </div>
          )}
          {!loading && results.length > 0 && (
            <ul className="py-1" data-testid="search-results">
              {results.map((r, i) => (
                <SearchResultItem
                  key={`${r.chapter_id}-${r.kind}-${i}`}
                  result={r}
                  index={i}
                  active={i === cursor}
                  query={query}
                  onHover={() => setCursor(i)}
                  onPick={() => pick(r)}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-2.5 border-t border-border bg-bone/60 text-[10px] uppercase tracking-wider text-muted-foreground">
          <div>
            <kbd className="bg-white border border-border rounded px-1 py-0.5 mx-0.5">↑</kbd>
            <kbd className="bg-white border border-border rounded px-1 py-0.5 mx-0.5">↓</kbd>
            navigate
            <kbd className="bg-white border border-border rounded px-1 py-0.5 ms-2 me-0.5">↵</kbd>
            open
          </div>
          <div>
            {results.length > 0 ? `${results.length} result${results.length === 1 ? "" : "s"}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
